import Card from "components/card";
import { useEffect, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { RevenueTransaction, RevenueDateRange } from "types/revenue";
import { revenueService } from "views/services/revenueService";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import CopyButton from "components/copyButton";
import useSWR from "swr";

interface RevenueTableProps {
  dateRange: RevenueDateRange;
  onTotalsUpdate: (
    totalTransactions: number,
    totalRevenueSol: number,
    solToUsdRate: number,
  ) => void;
}

const columnHelper = createColumnHelper<RevenueTransaction>();

function RevenueTable({ dateRange, onTotalsUpdate }: RevenueTableProps) {
  const [page, setPage] = useState(0);

  const formatDateForApi = (date: Date): string => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const { data: tableData, isLoading } = useSWR(
    `/revenue?startDate=${dateRange.startDate ? formatDateForApi(dateRange.startDate) : ""}&endDate=${dateRange.endDate ? formatDateForApi(dateRange.endDate) : ""}`,
    () =>
      revenueService.getRevenueData({
        startDate: dateRange.startDate
          ? formatDateForApi(dateRange.startDate)
          : undefined,
        endDate: dateRange.endDate
          ? formatDateForApi(dateRange.endDate)
          : undefined,
      }),
    {
      refreshInterval: 0,
      revalidateOnFocus: false,
    },
  );

  // Update totals when data changes
  useEffect(() => {
    if (tableData) {
      onTotalsUpdate(
        tableData.data.length,
        tableData.totalFeesSol,
        tableData.solToUsdRate,
      );
    }
  }, [tableData, onTotalsUpdate]);

  // Reset page when date range changes
  useEffect(() => {
    setPage(0);
  }, [dateRange]);

  const columns = [
    columnHelper.accessor((row, index) => index + 1, {
      id: "index",
      header: () => (
        <p className="w-[80px] text-sm font-bold text-gray-600">№</p>
      ),
      cell: (info) => (
        <p className="w-[80px] text-sm font-bold text-navy-700">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("user.username", {
      id: "creatorName",
      header: () => (
        <p className="w-[150px] text-sm font-bold text-gray-600">
          Creator Name
        </p>
      ),
      cell: (info) => (
        <p className="w-[150px] text-sm font-bold text-navy-700 text-wrap break-words">
          {info.getValue()}
        </p>
      ),
    }),
    columnHelper.accessor("walletAddress", {
      id: "walletAddress",
      header: () => (
        <p className="w-[200px] text-sm font-bold text-gray-600">
          Wallet Address
        </p>
      ),
      cell: (info) => {
        const address = info.getValue() as string;
        const truncatedAddress = `${address.slice(0, 6)}...${address.slice(-6)}`;
        return (
          <div className="w-[200px] flex items-center gap-2">
            <CopyButton textToCopy={address} />
            <p className="text-sm font-mono text-navy-700">
              {truncatedAddress}
            </p>
          </div>
        );
      },
    }),
    columnHelper.accessor("baseFeeSol", {
      id: "feeEstimate",
      header: () => (
        <p className="w-[120px] text-sm font-bold text-gray-600">Fee (SOL)</p>
      ),
      cell: (info) => (
        <p className="w-[120px] text-sm font-bold text-navy-700">
          {info.getValue().toFixed(6)} SOL
        </p>
      ),
    }),
  ];

  const data = tableData?.data ?? [];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    debugTable: true,
  });

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <Card extra={"w-full h-[800px] md:h-[600px] sm:overflow-auto px-6 pb-6"}>
      <header className="relative flex items-center gap-4 pt-4">
        <div className="text-xl font-bold text-navy-700">Fee Data</div>
      </header>

      <div className="mt-8 grow overflow-x-auto">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    className="bg-white pb-2 pr-4 pt-4 text-start"
                  >
                    <div className="flex items-center w-fit text-xs text-gray-200">
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td className="absolute left-1/2 -translate-x-1/2" colSpan={5}>
                  <p className="flex justify-center py-10">
                    <AiOutlineLoading3Quarters className="h-10 w-10 animate-spin" />
                  </p>
                </td>
              </tr>
            )}
            {!isLoading && (data.length ?? 0) <= 0 && (
              <tr>
                <td className="absolute left-1/2 -translate-x-1/2" colSpan={5}>
                  <p className="py-10 text-center font-bold uppercase">
                    No data
                  </p>
                </td>
              </tr>
            )}
            {!isLoading &&
              !!data &&
              data?.length > 0 &&
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="border-b last:border-b-0">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="py-2 pr-4 first:rounded-l-lg last:rounded-r-lg last:pr-0"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            {/* Totals Row */}
            {!isLoading && tableData && tableData.data.length > 0 && (
              <tr className="border-t-2 border-gray-400 bg-gray-50">
                <td className="py-3 pr-4 font-bold text-navy-700">
                  <p className="w-[80px] text-sm font-bold">
                    {formatNumber(tableData.data.length)}
                  </p>
                </td>
                <td className="py-3 pr-4">
                  <p className="w-[150px] text-sm font-bold text-navy-700">
                    TOTAL
                  </p>
                </td>
                <td className="py-3 pr-4">
                  <p className="w-[200px] text-sm text-gray-600"></p>
                </td>
                <td className="py-3 pr-4">
                  <div className="w-[120px]">
                    <p className="text-sm font-bold text-navy-700">
                      {tableData.totalFeesSol.toFixed(6)} SOL
                    </p>
                    <p className="text-xs text-gray-600">
                      {formatCurrency(
                        tableData.totalFeesSol * tableData.solToUsdRate,
                      )}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default RevenueTable;
